import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/", label: "Home", testId: "navbar-home-link" },
  { to: "/about", label: "About", testId: "navbar-about-link" },
  { to: "/services", label: "Services", testId: "navbar-services-link" },
  { to: "/portfolio", label: "Portfolio", testId: "navbar-portfolio-link" },
  { to: "/contact", label: "Contact", testId: "navbar-contact-link" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const closeMenu = () => setOpen(false);

  return (
    <header
      data-testid="navbar"
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-500",
        scrolled
          ? "backdrop-blur-xl bg-[#050505]/70 border-b border-white/10"
          : "bg-transparent border-b border-transparent",
      )}
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="navbar-logo"
          className="group flex items-center gap-2.5"
        >
          <span className="relative w-8 h-8 grid place-items-center rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 shadow-[0_0_24px_-4px_rgba(59,130,246,0.7)]">
            <span className="font-display font-bold text-white text-sm">C</span>
          </span>
          <span className="font-display text-base md:text-lg tracking-tight text-white">
            Coding<span className="text-blue-400">Solutions</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.testId}
              className={({ isActive }) =>
                cn(
                  "relative px-4 py-2 text-sm font-medium transition-colors",
                  isActive ? "text-white" : "text-zinc-400 hover:text-white",
                )
              }
            >
              {({ isActive }) => (
                <>
                  <span>{n.label}</span>
                  {isActive && (
                    <motion.span
                      layoutId="nav-underline"
                      className="absolute left-3 right-3 -bottom-0.5 h-px bg-blue-500"
                    />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link
            to="/contact"
            data-testid="navbar-cta-button"
            className="hidden md:inline-flex items-center gap-2 rounded-full bg-blue-500 hover:bg-blue-400 text-white text-sm font-medium px-5 py-2.5 transition-colors shadow-[0_8px_24px_-8px_rgba(59,130,246,0.7)]"
          >
            Start Your Project
            <span aria-hidden>→</span>
          </Link>
          <button
            data-testid="navbar-mobile-menu-toggle"
            aria-label="Toggle menu"
            onClick={() => setOpen((s) => !s)}
            className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-md border border-white/10 text-white"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="md:hidden overflow-hidden border-t border-white/10 bg-[#050505]/90 backdrop-blur-xl"
          >
            <div className="px-6 py-4 flex flex-col gap-1">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  onClick={closeMenu}
                  data-testid={`mobile-${n.testId}`}
                  className={({ isActive }) =>
                    cn(
                      "rounded-md px-3 py-3 text-base",
                      isActive
                        ? "bg-white/5 text-white"
                        : "text-zinc-300 hover:bg-white/5",
                    )
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                onClick={closeMenu}
                data-testid="mobile-navbar-cta-button"
                className="mt-2 inline-flex items-center justify-center rounded-full bg-blue-500 text-white text-sm font-medium px-5 py-3"
              >
                Start Your Project →
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
